import { getNumberWithSymbol } from '@shared/helpers/getNumberWithSymbol';

import type {
  LaunchpadSalePool,
  LaunchpadSalePoolType} from '@api/types/launchpadProjects';
import {
  LAUNCHPAD_SALE_POOL_TYPE
} from '@api/types/launchpadProjects';

interface PoolInfoData {
  typeName?: string;
  tooltip: {
    whitelist: Record<string, string>;
    noWhitelist: Record<string, string | ((param: { maxAllocation?: number }) => string)>;
  };
  whiteListName?: string;
  saleType?: string;
}

const POOLS_INFO: Record<LaunchpadSalePoolType, PoolInfoData> = {
  [LAUNCHPAD_SALE_POOL_TYPE.FCFS]: {
    typeName: 'FCFS',
    saleType: 'FCFS',
    whiteListName: 'Whitelist + FCFS',
    tooltip: {
      noWhitelist: {
        SQR: 'Deposit SQR in this IDO pool on a first come, first serve basis, open to all.',
        USDT: 'Deposit USDT in this IDO pool on a first come, first serve basis, open to everyone.',
        SQRp: 'Deposit SQRp in this IDO pool on a first come, first serve basis, open to all.',
      },
      whitelist: {
        USDT: 'Deposit USDT in this IDO pool on a first come, first serve basis, open to whitelisted users only.',
        SQR: 'Deposit SQR in this IDO pool on a first come, first serve basis, open to whitelisted users only.',
        SQRp: 'Deposit SQRp in this IDO pool on a first come, first serve basis, open to whitelisted users only.',
      },
    },
  },
  [LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED]: {
    typeName: 'FCFS + SQRp',
    saleType: 'SQRp',
    whiteListName: 'Whitelist + FCFS + SQRp',
    tooltip: {
      noWhitelist: {
        SQR: 'Deposit SQR in this IDO pool by matching each SQR with 1 SQRp point per equivalent USDT value, open to all on a first come, first serve basis.',
        USDT: 'Deposit USDT in this IDO pool by matching each USDT with 1 SQRp point, open to all on a first come, first serve basis.',
        SQRp: 'Deposit SQRp in this IDO pool by matching each SQRp with 1 SQRp point per equivalent USDT value, open to all on a first come, first serve basis.',
      },
      whitelist: {
        USDT: 'Deposit USDT in this IDO pool by matching each USDT with 1 SQRp point, open to whitelisted users only on a first come, first serve basis.',
        SQR: 'Deposit SQR in this IDO pool by matching each SQR with 1 SQRp point per equivalent USDT value, open to whitelisted users only on a first come, first serve basis.',
        SQRp: 'Deposit SQRp in this IDO pool by matching each SQRp with 1 SQRp point per equivalent USDT value, open to whitelisted users only on a first come, first serve basis.',
      },
    },
  },
  [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA]: {
    typeName: 'Pro-rata',
    saleType: 'ProRata',
    whiteListName: 'Whitelist + Pro-rata',
    tooltip: {
      noWhitelist: {
        SQR: 'Deposit SQR in this pro-rata IDO pool, with allocations determined by the total deposits.',
        USDT: ({ maxAllocation }) =>
          `Your allocation is based on the size of your deposit relative to the total deposits made by all users in the pool — the more you deposit, the larger your allocation.${
            maxAllocation
              ? ` You can deposit up to ${getNumberWithSymbol(maxAllocation)} USDT`
              : null
          }`,
        SQRp: 'Deposit SQRp in this pro-rata IDO pool, with allocations determined by the total deposits.',
      },
      whitelist: {
        USDT: 'Deposit USDT in this pro-rata IDO pool, with allocations determined by total deposits; use the Turbo Charge option by converting to SQR for larger allocations. Open to whitelisted users only.',
        SQR: 'Deposit SQR in this pro-rata IDO pool, with allocations determined by the total deposits. Open to whitelisted users only.',
        SQRp: 'Deposit SQRp in this pro-rata IDO pool, with allocations determined by the total deposits. Open to whitelisted users only.',
      },
    },
  },
  [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED]: {
    typeName: 'Pro-rata + SQRp',
    saleType: 'ProRataSQRp',
    whiteListName: 'Whitelist + Pro-rata + SQRp',
    tooltip: {
      noWhitelist: {
        SQR: 'Deposit SQR in this pro-rata IDO pool, with allocations determined by total deposits, and match each SQR with 1 SQRp point per equivalent USDT value.',
        USDT: 'Deposit USDT in this pro-rata IDO pool, with allocations determined by total deposits; boost your allocation with the Turbo Charge option by converting to SQR and matching each USDT value with 1 SQRp point.',
        SQRp: 'Deposit SQRp in this pro-rata IDO pool, with allocations determined by total deposits, and match each SQRp with 1 SQRp point per equivalent USDT value.',
      },
      whitelist: {
        USDT: 'Deposit USDT in this pro-rata IDO pool, with allocations determined by total deposits; boost your allocation with the Turbo Charge option by converting to SQR and matching each USDT value with 1 SQRp point. Open to whitelisted users only.',
        SQR: 'Deposit SQR in this pro-rata IDO pool, with allocations determined by total deposits, and match each SQR with 1 SQRp point per equivalent USDT value. Open to whitelisted users only.',
        SQRp: 'Deposit SQRp in this pro-rata IDO pool, with allocations determined by total deposits, and match each SQRp with 1 SQRp point per equivalent USDT value. Open to whitelisted users only.',
      },
    },
  },
};

export const getPoolInfo = (
  pool?: LaunchpadSalePool,
): Omit<PoolInfoData, 'tooltip'> & { tooltip: string; description?: string } => {
  const { type, description } = pool?.attributes || {};

  if (type && type in POOLS_INFO) {
    const info = POOLS_INFO[type];
    const isWhitelist = Boolean(pool?.attributes?.meta?.whiteListExists);
    const depositToken = pool?.attributes.depositTokenTicker || 'USDT';
    const tooltip = info.tooltip[isWhitelist ? 'whitelist' : 'noWhitelist'][depositToken];

    return {
      ...info,
      description,
      tooltip:
        typeof tooltip === 'function'
          ? tooltip({ maxAllocation: pool?.attributes.maxAllocation })
          : tooltip,
    };
  }

  return {
    typeName: 'N/A',
    tooltip: 'N/A',
  };
};
